import { useMutation, useQueryClient } from 'react-query';
import { apiService } from '~services/ApiService';
import { Notifier, NotifierComponents } from 'react-native-notifier';

// Types for response from the backend
export interface AttendanceStatusResponse {
  success: boolean;
  status: 'on_time' | 'late' | 'not_coming' | null;
  message?: string;
}

// Hook for managing "On Time" status
export const useOnTimeStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<AttendanceStatusResponse, Error, { order_id: string; is_on_time: boolean }>(
    async ({ order_id, is_on_time }) => {
      // If is_on_time is false, use DELETE endpoint, otherwise use POST
      if (is_on_time) {
        return await apiService.post<AttendanceStatusResponse>(`orders/${order_id}/on-time`);
      } else {
        return await apiService.delete<AttendanceStatusResponse>(`orders/${order_id}/on-time`);
      }
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries(['orderById', variables.order_id]);
        
        if (variables.is_on_time ) {
        Notifier.showNotification({
          title: "Great! Have a wonderful evening",
         // description: data.message || '',
          Component: NotifierComponents.Alert,
          duration: 5000,
          componentProps: {
            alertType: 'success',
          },
        });
        }
      },
      onError: (error) => {
        console.error('Error updating on time status:', error);
        Notifier.showNotification({
          title: 'Failed to update your status',
          description: error.message,
          Component: NotifierComponents.Alert,
          componentProps: {
            alertType: 'error',
          },
        });
      },
    },
  );
};

// Hook for managing "Late" status
export const useLateStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<AttendanceStatusResponse, Error, { order_id: string; is_late: boolean }>(
    async ({ order_id, is_late }) => {
      // If is_late is false, use DELETE endpoint, otherwise use POST
      if (is_late) {
        return await apiService.post<AttendanceStatusResponse>(`orders/${order_id}/late`);
      } else {
        return await apiService.delete<AttendanceStatusResponse>(`orders/${order_id}/late`);
      }
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries(['orderById', variables.order_id]);
        
        if (variables.is_late ) {
          
        Notifier.showNotification({
          title: "We’re sorry to hear that — we’ll let the other guests know",
          // description: data.message || '',
          Component: NotifierComponents.Alert,
          duration: 5000,
          componentProps: {
            alertType: 'warn',
          },
        });
        }
      },
      onError: (error) => {
        console.error('Error updating late status:', error);
        Notifier.showNotification({
          title: 'Failed to update your status',
          description: error.message,
          Component: NotifierComponents.Alert,
          componentProps: {
            alertType: 'error',
          },
        });
      },
    },
  );
};

// Hook for managing "Not Coming" status
export const useNotComingStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<AttendanceStatusResponse, Error, { order_id: string; is_not_coming: boolean }>(
    async ({ order_id, is_not_coming }) => {
      // If is_not_coming is false, use DELETE endpoint, otherwise use POST
      if (is_not_coming) {
        return await apiService.post<AttendanceStatusResponse>(`orders/${order_id}/not-coming`);
      } else {
        return await apiService.delete<AttendanceStatusResponse>(`orders/${order_id}/not-coming`);
      }
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries(['orderById', variables.order_id]);
        
        // Show success notification
        const message = variables.is_not_coming 
          ? "You've set your status to 'I'm not coming anymore'"
          : "You've removed your 'not coming' status";
        
          if ( variables.is_not_coming) {
            
        Notifier.showNotification({
          title: "Oh. That’s not good. But we hope you’re OK. We will inform the other guests you are no longer coming",
          // description: data.message || '',
          Component: NotifierComponents.Alert,
          duration: 5000,
          componentProps: {
            alertType: 'error',
          },
        });
          }
          
      },
      onError: (error) => {
        console.error('Error updating not coming status:', error);
        Notifier.showNotification({
          title: 'Failed to update your status',
          description: error.message,
          Component: NotifierComponents.Alert,
          componentProps: {
            alertType: 'error',
          },
        });
      },
    },
  );
};